import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@/utils/library/languages";

import routesEN from "../../public/locales/en/routes.json";
import routesES from "../../public/locales/es/routes.json";
import routesPT from "../../public/locales/pt/routes.json";
import routesFR from "../../public/locales/fr/routes.json";
import routesJA from "../../public/locales/ja/routes.json";
import routesDE from "../../public/locales/de/routes.json";

/** English segment key → localized URL segment for that language */
export const ROUTES_BY_LANG: Record<
  SupportedLanguage,
  Record<string, string>
> = {
  en: routesEN,
  es: routesES,
  pt: routesPT,
  fr: routesFR,
  ja: routesJA,
  de: routesDE,
};

/** Pre-built reverse maps: localized segment → English key, per language. */
const REVERSE_ROUTES_BY_LANG: Record<SupportedLanguage, Record<string, string>> =
  Object.fromEntries(
    (Object.entries(ROUTES_BY_LANG) as [SupportedLanguage, Record<string, string>][]).map(
      ([lang, map]) => [
        lang,
        Object.fromEntries(
          Object.entries(map).map(([engKey, translated]) => [translated, engKey]),
        ),
      ],
    ),
  ) as Record<SupportedLanguage, Record<string, string>>;

function splitPath(path: string): string[] {
  return path.split("/").filter(Boolean);
}

function joinPath(segments: string[]): string {
  if (segments.length === 0) return "";
  return `/${segments.join("/")}`;
}

/**
 * Translate a path that uses English filesystem segments into the localized
 * segments for `lang` (e.g. /product → /producto for es).
 */
export function getTranslatedPath(englishPath: string, lang: string): string {
  const segments = splitPath(englishPath);
  const map = ROUTES_BY_LANG[lang as SupportedLanguage] ?? ROUTES_BY_LANG.en;
  const out = segments.map((seg) =>
    Object.hasOwn(map, seg) ? map[seg] : seg,
  );
  return joinPath(out);
}

/**
 * Map a localized path (after `lang`) to English filesystem segments.
 * Unknown segments are left unchanged (blog slugs, partner slugs, industry slugs).
 */
export function getEnglishPath(localizedPath: string, lang: string): string {
  const segments = splitPath(localizedPath);
  const reverseMap =
    REVERSE_ROUTES_BY_LANG[lang as SupportedLanguage] ??
    REVERSE_ROUTES_BY_LANG.en;

  const english = segments.map((seg) =>
    Object.hasOwn(reverseMap, seg) ? reverseMap[seg] : seg,
  );

  return joinPath(english);
}

/**
 * For hreflang / sitemap: full path suffix (starting with /) per language for a
 * route defined with English segments (e.g. "/product", "/partner/acme").
 */
export function getTranslatedPathsByLang(
  englishSuffix: string,
): Record<string, string> {
  let normalized = "";
  if (englishSuffix !== "" && englishSuffix !== "/") {
    normalized = englishSuffix.startsWith("/")
      ? englishSuffix
      : `/${englishSuffix}`;
  }

  return Object.fromEntries(
    SUPPORTED_LANGUAGES.map((l) => [
      l,
      getTranslatedPath(normalized, l),
    ]),
  );
}

/**
 * Whether the current URL corresponds to a marketing route defined with English
 * segments (e.g. `/es/producto` matches englishRoute `/product`).
 */
export function pathMatchesEnglishRoute(
  pathname: string | null | undefined,
  lang: string,
  englishRoute: string,
): boolean {
  if (!pathname) return false;
  const normalized = englishRoute.startsWith("/")
    ? englishRoute
    : `/${englishRoute}`;
  const translated = getTranslatedPath(normalized, lang);
  const withTranslatedSegments = `/${lang}${translated}`;
  const withEnglishSegments = `/${lang}${normalized}`;
  return (
    pathname === withTranslatedSegments ||
    pathname.startsWith(`${withTranslatedSegments}/`) ||
    pathname === withEnglishSegments ||
    pathname.startsWith(`${withEnglishSegments}/`)
  );
}
